/**
 * ScorecardEditorPage.tsx — Admin → Account → Scorecards → edit/create
 *
 * Template editor: name, (fixed) type/team scope, scoring prompt, optional
 * formatting prompt + summary-takeaways rules, and a reorderable list of
 * weighted fields (framer-motion Reorder). Save persists the template and its
 * full ordered fields array via the scorecards service.
 *
 * Locked (plan 2026-10-10-001 U7: R8, R18) — this page follows the
 * `scorecards` level: the scorecard shows read-only (an AdminLockedFieldset)
 * with a single Close; fields have no grip, Add, Edit or Delete, and a View
 * icon opens a field's criteria read-only. There is no new scorecard to make,
 * so /admin/scorecards/new goes back to the list.
 */
import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Reorder, useDragControls } from 'framer-motion';
import { Eye, GripVertical, Pencil, Plus, Trash2, Weight } from 'lucide-react';
import { AdminLockedFieldset } from '@/components/admin/AdminLockedFieldset';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { FormField } from '@/components/ui/FormField';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { toast } from '@/store/toastStore';
import * as scorecardsSvc from '@/services/scorecards.service';

const SCORE_OPTIONS = Array.from({ length: 10 }, (_, i) => ({ label: String(i + 1), value: i + 1 }));

interface EditableField {
  _uid: string;
  name: string;
  minCriteria: string;
  midCriteria: string;
  maxCriteria: string;
  weight: number;
}

function uid(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `f-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
  }
}

// ─── One draggable field row (dedicated drag handle) ─────────────────────────

function FieldItem({
  field,
  locked,
  onEdit,
  onDelete,
}: {
  field: EditableField;
  /** Locked page: no grip, Edit or Delete; a View icon opens the field read-only. */
  locked: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const controls = useDragControls();
  return (
    <Reorder.Item
      value={field}
      dragListener={false}
      dragControls={controls}
      className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-3"
    >
      {!locked && (
        <button
          type="button"
          onPointerDown={(e) => controls.start(e)}
          className="cursor-grab touch-none text-slate-300 hover:text-slate-500 active:cursor-grabbing"
          title="Drag to reorder"
        >
          <GripVertical className="h-4 w-4" />
        </button>
      )}
      <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">
        {field.name || <span className="italic text-slate-400">Untitled field</span>}
      </span>
      <span className="inline-flex items-center gap-1 text-xs text-slate-400" title="Weight">
        <Weight className="h-3.5 w-3.5" />
        {field.weight}
      </span>
      {locked ? (
        <button
          type="button"
          onClick={onEdit}
          className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          title="View field"
          aria-label={`View ${field.name || 'field'}`}
        >
          <Eye className="h-4 w-4" />
        </button>
      ) : (
        <>
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            title="Edit field"
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-400 hover:bg-red-50 hover:text-red-600"
            title="Delete field"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </>
      )}
    </Reorder.Item>
  );
}

// ─── Field add/edit modal ────────────────────────────────────────────────────

function FieldModal({
  field,
  ratingMin,
  ratingMid,
  ratingMax,
  onClose,
  onSave,
}: {
  field: EditableField;
  ratingMin: number;
  ratingMid: number | null;
  ratingMax: number;
  onClose: () => void;
  onSave: (f: EditableField) => void;
}) {
  const [name, setName] = useState(field.name);
  const [minCriteria, setMinCriteria] = useState(field.minCriteria);
  const [midCriteria, setMidCriteria] = useState(field.midCriteria);
  const [maxCriteria, setMaxCriteria] = useState(field.maxCriteria);
  const [weight, setWeight] = useState(String(field.weight));
  // Locked page: a viewer, titled without "Edit", with Close only.
  const locked = useAdminPageLevel() === 'locked';

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={locked ? 'Field' : field.name ? 'Edit Field' : 'Add Field'}
      size="lg"
      footer={
        locked ? (
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button
              onClick={() => {
                if (!name.trim()) {
                  toast.error('Field name is required');
                  return;
                }
                onSave({
                  ...field,
                  name: name.trim(),
                  minCriteria: minCriteria.trim(),
                  midCriteria: midCriteria.trim(),
                  maxCriteria: maxCriteria.trim(),
                  weight: Math.max(1, parseInt(weight, 10) || 1),
                });
              }}
            >
              Save Field
            </Button>
          </>
        )
      }
    >
      <AdminLockedFieldset>
        <div className="space-y-4">
          <FormField label="Field Name" required={!locked}>
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={255} />
          </FormField>
          <FormField label={`Minimum score | ${ratingMin}`} helpText="What a low score for this field looks like.">
            <Textarea
              value={minCriteria}
              onChange={(e) => setMinCriteria(e.target.value)}
              rows={3}
              placeholder="e.g. No self-introduction or vague identification."
            />
          </FormField>
          {ratingMid !== null && (
            <FormField label={`Middle score | ${ratingMid}`} helpText="Optional — what a mid-range score looks like.">
              <Textarea
                value={midCriteria}
                onChange={(e) => setMidCriteria(e.target.value)}
                rows={3}
                placeholder="e.g. Partial or delayed introduction."
              />
            </FormField>
          )}
          <FormField label={`Maximum score | ${ratingMax}`} helpText="What a top score for this field looks like.">
            <Textarea
              value={maxCriteria}
              onChange={(e) => setMaxCriteria(e.target.value)}
              rows={3}
              placeholder="e.g. Provider clearly states full name, title, and role within the first 5 minutes."
            />
          </FormField>
          <FormField label="Score Weight" helpText="Relative importance in the overall score.">
            <Input
              type="number"
              min={1}
              max={100}
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              className="w-28"
            />
          </FormField>
        </div>
      </AdminLockedFieldset>
    </Modal>
  );
}

// ─── Editor page ─────────────────────────────────────────────────────────────

export function ScorecardEditorPage() {
  const navigate = useNavigate();
  const locked = useAdminPageLevel() === 'locked';
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const numericId = isNew ? 0 : Number(id);

  const [name, setName] = useState('');
  const [scoringPrompt, setScoringPrompt] = useState('');
  const [formattingPrompt, setFormattingPrompt] = useState('');
  const [summaryTakeawaysRules, setSummaryTakeawaysRules] = useState('');
  const [ratingMin, setRatingMin] = useState(1);
  const [ratingMid, setRatingMid] = useState<number | null>(null);
  const [ratingMax, setRatingMax] = useState(5);
  const [active, setActive] = useState(true);
  const [fields, setFields] = useState<EditableField[]>([]);
  const [editingField, setEditingField] = useState<EditableField | null>(null);
  const [saving, setSaving] = useState(false);

  const { data: template, isLoading } = useQuery({
    queryKey: ['admin-scorecard', numericId],
    queryFn: () => scorecardsSvc.getScorecard(numericId),
    enabled: !isNew,
  });

  useEffect(() => {
    if (!template) return;
    setName(template.name);
    setScoringPrompt(template.scoringPrompt);
    setFormattingPrompt(template.formattingPrompt);
    setSummaryTakeawaysRules(template.summaryTakeawaysRules);
    setRatingMin(template.ratingMin);
    setRatingMid(template.ratingMid);
    setRatingMax(template.ratingMax);
    setActive(template.active);
    setFields(
      template.fields.map((f) => ({
        _uid: uid(),
        name: f.name,
        minCriteria: f.minCriteria,
        midCriteria: f.midCriteria,
        maxCriteria: f.maxCriteria,
        weight: f.weight,
      })),
    );
  }, [template]);

  const canSave = useMemo(() => name.trim().length > 0, [name]);

  async function handleSave() {
    if (locked) return;
    if (!canSave) {
      toast.error('Scorecard name is required');
      return;
    }
    setSaving(true);
    const payload: scorecardsSvc.ScorecardTemplatePayload = {
      name: name.trim(),
      scoringPrompt,
      formattingPrompt,
      summaryTakeawaysRules,
      ratingMin,
      ratingMid,
      ratingMax,
      active,
      fields: fields.map((f) => ({
        name: f.name,
        minCriteria: f.minCriteria,
        midCriteria: f.midCriteria,
        maxCriteria: f.maxCriteria,
        weight: f.weight,
      })),
    };
    try {
      if (isNew) {
        await scorecardsSvc.createScorecard(payload);
        toast.success('Scorecard created');
      } else {
        await scorecardsSvc.updateScorecard(numericId, payload);
        toast.success('Scorecard saved');
      }
      navigate('/admin/scorecards');
    } catch {
      toast.error('Failed to save scorecard');
    } finally {
      setSaving(false);
    }
  }

  function handleSaveField(updated: EditableField) {
    setFields((prev) => {
      const exists = prev.some((f) => f._uid === updated._uid);
      return exists ? prev.map((f) => (f._uid === updated._uid ? updated : f)) : [...prev, updated];
    });
    setEditingField(null);
  }

  // Locked: there is no new scorecard to make.
  if (locked && isNew) return <Navigate to="/admin/scorecards" replace />;

  if (!isNew && isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <PageHeader
        title={isNew ? 'New Scorecard' : name || 'Scorecard'}
        actions={
          locked ? (
            <Button variant="secondary" onClick={() => navigate('/admin/scorecards')}>
              Close
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => navigate('/admin/scorecards')} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={handleSave} loading={saving} disabled={!canSave}>
                {isNew ? 'Create Scorecard' : 'Save Changes'}
              </Button>
            </div>
          )
        }
      />

      <div className="mb-6 flex flex-wrap items-center gap-2">
        <Badge variant="info" size="sm">Conversation</Badge>
        <Badge variant="neutral" size="sm">All Teams</Badge>
      </div>

      <AdminLockedFieldset>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Left: template config */}
          <div className="space-y-4">
            <FormField label="Name" required={!locked}>
              <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={255} />
            </FormField>

            <div>
              <h3 className="mb-2 text-sm font-semibold text-slate-900">Field Properties</h3>
              <div className="grid grid-cols-3 gap-3">
                <FormField label="Min score">
                  <Select
                    options={SCORE_OPTIONS}
                    value={ratingMin}
                    onChange={(v) => v != null && setRatingMin(v)}
                  />
                </FormField>
                <FormField label="Max Score">
                  <Select
                    options={SCORE_OPTIONS}
                    value={ratingMax}
                    onChange={(v) => v != null && setRatingMax(v)}
                  />
                </FormField>
                <FormField label="Mid Score (Optional)">
                  <Select
                    options={SCORE_OPTIONS}
                    value={ratingMid}
                    onChange={setRatingMid}
                    clearable
                    placeholder="None"
                  />
                </FormField>
              </div>
            </div>

            <FormField label="Scoring Prompt" helpText="Overall guidance for how the AI should evaluate the conversation.">
              <Textarea value={scoringPrompt} onChange={(e) => setScoringPrompt(e.target.value)} rows={5} />
            </FormField>
            <FormField label="Formatting Prompt" helpText="Optional — how feedback bullets should be written.">
              <Textarea value={formattingPrompt} onChange={(e) => setFormattingPrompt(e.target.value)} rows={6} />
            </FormField>
            <FormField label="Summary Takeaways Rules" helpText="Optional — rules for the call summary takeaways. Leave blank to use the default.">
              <Textarea value={summaryTakeawaysRules} onChange={(e) => setSummaryTakeawaysRules(e.target.value)} rows={4} />
            </FormField>
          </div>

          {/* Right: fields */}
          <div>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">Fields</h2>
              {!locked && (
                <Button
                  size="sm"
                  onClick={() =>
                    setEditingField({ _uid: uid(), name: '', minCriteria: '', midCriteria: '', maxCriteria: '', weight: 1 })
                  }
                >
                  <Plus className="mr-1 h-4 w-4" />
                  Add field
                </Button>
              )}
            </div>

            {fields.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-200 py-12 text-center text-sm text-slate-500">
                {locked ? 'No fields yet.' : 'No fields yet. Add at least one field to score.'}
              </div>
            ) : (
              <Reorder.Group axis="y" values={fields} onReorder={locked ? () => {} : setFields} className="space-y-2">
                {fields.map((f) => (
                  <FieldItem
                    key={f._uid}
                    field={f}
                    locked={locked}
                    onEdit={() => setEditingField(f)}
                    onDelete={() => setFields((prev) => prev.filter((x) => x._uid !== f._uid))}
                  />
                ))}
              </Reorder.Group>
            )}
          </div>
        </div>
      </AdminLockedFieldset>

      {editingField && (
        <FieldModal
          field={editingField}
          ratingMin={ratingMin}
          ratingMid={ratingMid}
          ratingMax={ratingMax}
          onClose={() => setEditingField(null)}
          onSave={handleSaveField}
        />
      )}
    </div>
  );
}

export default ScorecardEditorPage;
